import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Package, Box, Wrench, ArrowRight } from 'lucide-react';
import { useTrucks } from '../hooks/useTrucks';

const TruckCategoriesPage = () => {
  const { trucks, loading } = useTrucks();

  // Count trucks by category
  const getCategoryCount = (bodyType) => {
    if (loading || !trucks) return 0;
    return trucks.filter(truck => 
      truck.bodytype && truck.bodytype.toLowerCase().includes(bodyType.toLowerCase())
    ).length;
  };

  const categories = [
    {
      title: 'Tractor Units',
      description: 'Professional tractor units for long-haul operations. Perfect for pulling semi-trailers with various power options and cab configurations.',
      icon: Truck,
      color: 'orange',
      link: '/trucks/tractor-units',
      count: getCategoryCount('tractor'),
      features: ['Euro-6 Compliance', 'Sleeper & Day Cabs', '400-600 HP Options', 'Advanced Safety Systems']
    },
    {
      title: 'Rigid Vehicles',
      description: '7.5 to 26 ton rigid vehicles with various body types. Ideal for regional distribution and urban deliveries.',
      icon: Package,
      color: 'blue',
      link: '/trucks/rigids',
      count: getCategoryCount('rigid'),
      features: ['7.5t - 26t Range', 'Multiple Body Types', 'City Access', 'Flexible Configurations']
    },
    {
      title: 'Chassis Cabs',
      description: 'Bare chassis ready for custom body fitting. Perfect for specialised applications and bespoke requirements.',
      icon: Wrench,
      color: 'purple',
      link: '/trucks/chassis-cabs',
      count: getCategoryCount('chassis'),
      features: ['Custom Body Options', 'Flexible Specifications', 'Specialised Applications', 'Bespoke Solutions']
    },
    {
      title: 'Curtainsides',
      description: 'Versatile curtainside vehicles offering flexible loading access. Perfect for palletized goods with side and rear loading capabilities.',
      icon: Package,
      color: 'green',
      link: '/trucks/curtainsides',
      count: getCategoryCount('curtain'),
      features: ['Side Loading Access', 'Flexible Cargo', 'Weather Protection', 'Fast Loading']
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      orange: 'bg-orange-600 text-white hover:bg-orange-700',
      blue: 'bg-blue-600 text-white hover:bg-blue-700',
      green: 'bg-green-600 text-white hover:bg-green-700',
      purple: 'bg-purple-600 text-white hover:bg-purple-700'
    };
    return colors[color] || colors.orange;
  };

  const getBgColorClasses = (color) => {
    const colors = {
      orange: 'bg-orange-100',
      blue: 'bg-blue-100',
      green: 'bg-green-100',
      purple: 'bg-purple-100'
    };
    return colors[color] || colors.orange;
  };

  const getTextColorClasses = (color) => {
    const colors = {
      orange: 'text-orange-600',
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600'
    };
    return colors[color] || colors.orange;
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section 
        className="bg-black text-white py-16 relative"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_radius-consistency/artifacts/hb14utjx_before.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-black mb-6 font-inter">
              Truck <span className="text-orange-500">Categories</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Find the perfect commercial vehicle for your specific needs. Browse our comprehensive range organised by vehicle type and application.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-orange-300 hover:shadow-lg transition-all group">
                  <div className="flex items-start gap-6">
                    <div className={`${getBgColorClasses(category.color)} rounded-full p-4 group-hover:scale-110 transition-transform`}>
                      <Icon className={`h-8 w-8 ${getTextColorClasses(category.color)}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-2xl font-black text-black font-inter">{category.title}</h2>
                        <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-bold">
                          {category.count} Available
                        </div>
                      </div>
                      <p className="text-gray-600 font-inter mb-4 leading-relaxed">
                        {category.description}
                      </p>
                      
                      {/* Features */}
                      <div className="mb-6">
                        <h4 className="text-sm font-bold text-gray-700 mb-2 font-inter">Key Features:</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {category.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600">
                              <div className={`w-2 h-2 rounded-full ${category.color === 'orange' ? 'bg-orange-600' : category.color === 'blue' ? 'bg-blue-600' : category.color === 'green' ? 'bg-green-600' : 'bg-purple-600'}`}></div>
                              <span className="font-inter">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <Link 
                        to={category.link}
                        className={`${getColorClasses(category.color)} px-6 py-3 rounded font-bold transition-colors inline-flex items-center gap-2`}
                      >
                        Browse {category.title}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Browse All CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black text-black mb-6 font-inter">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-lg text-gray-600 mb-8 font-inter max-w-2xl mx-auto">
            Browse our complete inventory or contact our expert team for personalised assistance in finding the perfect commercial vehicle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/trucks"
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded font-bold text-lg transition-colors inline-flex items-center justify-center"
            >
              Browse All Trucks
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link 
              to="/contact"
              className="bg-black hover:bg-gray-800 text-white px-8 py-4 rounded font-bold text-lg transition-colors"
            >
              Contact Our Experts
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Lawman */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-black mb-4 font-inter">
              Why Choose <span className="text-orange-600">Lawman Commercials</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-2xl font-bold">30+</div>
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Years Experience</h3>
              <p className="text-gray-600 font-inter">Over three decades of commercial vehicle expertise.</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-2xl font-bold">✓</div>
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Quality Assured</h3>
              <p className="text-gray-600 font-inter">All vehicles thoroughly inspected and tested.</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-2xl font-bold">24/7</div>
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Support Available</h3>
              <p className="text-gray-600 font-inter">Round-the-clock assistance when you need it.</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-lg font-bold">MAN</div>
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Brand Specialist</h3>
              <p className="text-gray-600 font-inter">Independent MAN brand expertise and knowledge.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TruckCategoriesPage;